import BackgroundImage from "@components/BackgroundImageCst";
import { Box, Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import Breadcrumb from "@components/Breadcrumb";
import HeroCst from "@components/HeroCst";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Table from "@components/Table";
import { PagesID } from "@utils/utilsConfigurations";
import { useFetchTable } from "@hooks/useFetchDataTable";
import { ServicesURL } from "@utils/utilsApi";
import ButtonBackPage from "@components/ButtonBackPage";
import { useParams } from "react-router-dom";
import ModalCst from "@components/ModalCst";
import { useMemo, useState } from "react";
import { ActionsType } from "src/interfaces/Common";
import CustomRowAction from "@components/Table/components/CustomRowAction";
import { usePostDetail } from "@hooks/useFetchDetail";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import { useAuth } from "@context/auth";
import { RolesDigitedEnum } from "@context/auth/types";
import { useFilters } from "@context/filters";

const UsersRolesAssociation: React.FC = () => {
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    key?: string | number;
  }>();
  const { t } = useTranslation(["dashboard-users-roles-page", "common"]);
  const { id = "1" } = useParams();
  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["dashboard.corporate-association"] + "user"}`;
  const [associateRoles, setAssociateRoles] = useState(false);
  const [dashboarItem, setDashboardItem] = useState<any>(null);
  const isPO = user?.roles.includes(RolesDigitedEnum.PRODUCT_OWNER);

  const { columns, dataTable, actionsTopToolbar, actionForRow, mutate } =
    useFetchTable(
      `${ServicesURL.getDashboard}?getUsersAndRoles=true&corporateId=${id}${isPO ? "" : `&userId=${user?.id_user}`}`,
      PagesID["corporate.dashboard.users-roles"],
      filters[filterId],
    );

  const { trigger } = usePostDetail(ServicesURL.associateRoles);

  const onRolesAssociation = async (selectedRoles: any, list: any) => {
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });

    const rolesSelected = selectedRoles.map((role: any) => {
      return {
        roleId: role.id,
        roleName: role.roleKey,
      };
    });

    const listToSend = list.filter((obj1: any) =>
      rolesSelected.some((obj2: any) => obj1.id === obj2.roleId),
    );

    const modifiedList = listToSend.map((item: any) => {
      return {
        roleId: item.id,
        roleName: item.roleKey,
      };
    });

    const idsAssociated = modifiedList.map((item: any) => item.roleName);

    const idsToAdd = idsAssociated.filter(
      (item: any) => !dashboarItem.associatedRoles.includes(item),
    );

    const idsToDelete = dashboarItem.associatedRoles.filter((item: any) => {
      return !idsAssociated.includes(item);
    });

    const toSend: any = [...idsToAdd, ...idsToDelete];

    const finalRoles = list
      .filter((obj1: any) => toSend.some((obj2: any) => obj1.roleKey === obj2))
      .map((finalItem: any) => {
        return {
          roleId: finalItem.id,
          roleName: finalItem.roleKey,
        };
      });

    const objToSend: any = {
      corporate: {
        corporateId: id,
      },
      dashboardId: dashboarItem?.id,
      roles: finalRoles,
    };

    try {
      await trigger(objToSend);
      setOpenToast({ s: StatusEnum.OK, message: "" });
      mutate();
    } catch (error) {
      console.error(error);
      setOpenToast(undefined);
    }
  };

  const configurationActionForRow: ActionsType[] = useMemo(() => {
    return actionForRow?.map((action: any) => {
      switch (action.type) {
        case "button": {
          return {
            ...action,
            callBack: (row: any) => {
              setAssociateRoles(true);
              setDashboardItem(row);
            },
          };
        }
        default:
          return action;
      }
    });
  }, [actionForRow]);

  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = configurationActionForRow && [
      ...configurationActionForRow,
    ];

    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
      />,
    ];
  };

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-dashboard.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst title={t("hero-title")} description={t("hero-description")} />
        <ButtonBackPage />
        <Box
          sx={{
            mt: "25px",
          }}
        >
          <Table
            id={PagesID["dashboard.corporate-association"] + "user"}
            data={dataTable?.output}
            columns={columns || []}
            configurationTopToolbar={actionsTopToolbar}
            configurationActionForRow={actionForRow}
            renderRowActions={customRenderRowActions}
            totalPages={dataTable?.metadata?.total_elements}
            modalTitle={t("hero-title")?.replace("-", "") || ""}
          />
        </Box>
        <ModalCst
          selectLabel={"Ruoli"}
          title="Che ruolo vuoi associare ?"
          description=" "
          open={associateRoles}
          setOpen={setAssociateRoles}
          typeOfModal={"dashboardRoles"}
          service={"getRoles"}
          dashboarItem={dashboarItem}
          onSubmit={onRolesAssociation}
        />
        <ModalConfirmsCst
          open={!!openToast}
          title={t("hero-title-add")}
          description={""}
          onCallBackClosed={() => {
            setOpenToast(undefined);
            setDashboardItem(null);
            setAssociateRoles(false);
          }}
          status={openToast}
          routeToSamePage
        />
      </Container>
    </AnimationFadeIn>
  );
};

export default UsersRolesAssociation;
